import React from 'react';
import { CheckCircle2, MessageSquareQuote, MapPin, BookOpen, HelpCircle } from 'lucide-react';

/**
 * VerifiedAnswerCard — landing-page style Q&A layout (P86.2).
 *
 * Three sections only:
 *   1. QUESTION card (cyan chip + user's question, matches landing hero cyan)
 *   2. ANSWER card with three inner blocks:
 *        • Answer (yellow "ANSWER" eyebrow · big verdict sentence)
 *        • Where It Was Found (blue icon · citation chips)
 *        • Explanation (green icon · plain English)
 *   3. (optional) Clarifying prompt if confidence < 55
 *
 * All spacing, radii, and eyebrow-label typography mirror the landing page
 * (POPULAR EXAMPLES / TRY ONE OF THESE style).
 */
export default function VerifiedAnswerCard({ answer, question }) {
  if (!answer || typeof answer !== 'object') return null;

  const q = (question || answer.question_asked || '').trim();

  const verified = answer.verified_answer
    || answer.key_takeaway
    || firstSentence(answer.explanation)
    || 'AskStewie could not verify a direct answer to this question.';

  const src = normalizeSource(answer);
  const confidence = pickConfidence(answer);
  const supporting = answer.supporting_language
    || (typeof answer.exact_contract_wording === 'string' ? answer.exact_contract_wording : answer.exact_contract_wording?.quote)
    || answer.rule?.text
    || '';
  const explanation = answer.explanation || answer.what_this_means_for_you || '';

  const confTone = confidence >= 90 ? 'green' : confidence >= 70 ? 'cyan' : confidence >= 55 ? 'yellow' : 'orange';
  const confLabel = confidence >= 95 ? 'Verified' : confidence >= 70 ? 'High confidence' : confidence >= 55 ? 'Moderate confidence' : 'Low · verify';

  const showQuestion = q.length > 0;
  const showFound = !!(src.document || supporting);
  const showExplanation = !!explanation;
  const showClarify = !!(answer.needs_clarification && answer.clarifying_question) || confidence < 55;

  return (
    <div className="vac-root" data-testid="verified-answer-card">
      <style>{styles}</style>

      {/* Question card */}
      {showQuestion && (
        <div className="vac-question" data-testid="verified-answer-question">
          <span className="vac-q-chip">
            <MessageSquareQuote size={13} strokeWidth={2.4} />
            QUESTION
          </span>
          <span className="vac-q-text">{q}</span>
        </div>
      )}

      {/* Answer card */}
      <article className="vac-answer-card" data-testid="verified-answer-answer">

        {/* Confidence badge in the top-right corner */}
        <span className={`vac-conf vac-conf--${confTone}`}>
          <CheckCircle2 size={12} strokeWidth={2.6} />
          <span className="vac-conf-value">{Math.round(confidence)}%</span>
          <span className="vac-conf-label">{confLabel}</span>
        </span>

        {/* Section 1 — Answer */}
        <section className="vac-block vac-block--answer">
          <span className="vac-eyebrow vac-eyebrow--yellow">ANSWER</span>
          <p className="vac-sentence" data-testid="verified-answer-sentence">{verified}</p>
        </section>

        {/* Section 2 — Where It Was Found */}
        {showFound && (
          <section className="vac-block" data-testid="verified-answer-source">
            <div className="vac-eyebrow-row">
              <span className="vac-icon vac-icon--blue"><MapPin size={13} strokeWidth={2.4} /></span>
              <span className="vac-eyebrow">WHERE IT WAS FOUND</span>
            </div>
            <div className="vac-source-chips">
              {src.document && src.document !== 'N/A' && (
                <SourceChip label="DOCUMENT" value={src.document} main />
              )}
              {src.article && src.article !== 'N/A' && (
                <SourceChip label="ARTICLE" value={src.article} />
              )}
              {src.section && src.section !== 'N/A' && (
                <SourceChip label="SECTION" value={src.section} />
              )}
              {src.page && src.page !== 'N/A' && (
                <SourceChip label="PAGE" value={src.page} />
              )}
            </div>
            {supporting && (
              <blockquote className="vac-quote">
                “{truncateWords(supporting, 320)}”
              </blockquote>
            )}
          </section>
        )}

        {/* Section 3 — Explanation */}
        {showExplanation && (
          <section className="vac-block" data-testid="verified-answer-explanation">
            <div className="vac-eyebrow-row">
              <span className="vac-icon vac-icon--green"><BookOpen size={13} strokeWidth={2.4} /></span>
              <span className="vac-eyebrow">EXPLANATION</span>
            </div>
            <p className="vac-para">{explanation}</p>
          </section>
        )}

        {/* Optional — clarifying question */}
        {showClarify && (
          <section className="vac-clarify" data-testid="verified-answer-clarify">
            <span className="vac-icon vac-icon--orange"><HelpCircle size={13} strokeWidth={2.4} /></span>
            <span className="vac-clarify-body">
              <span className="vac-eyebrow vac-eyebrow--orange">ONE QUICK CLARIFICATION</span>
              <span className="vac-clarify-text">
                {answer.clarifying_question || 'Can you share a few more details so I can pin this to the exact contract language?'}
              </span>
            </span>
          </section>
        )}
      </article>
    </div>
  );
}

function SourceChip({ label, value, main = false }) {
  return (
    <span className={`vac-src-chip ${main ? 'vac-src-chip--main' : ''}`}>
      <span className="vac-src-lbl">{label}</span>
      <span className="vac-src-val">{value}</span>
    </span>
  );
}

/* ── Helpers (unchanged from prior) ────────────────────────────────── */

function normalizeSource(a) {
  const s = a.source || {};
  const cite = a.rule?.citation || {};
  const primary = Array.isArray(a.sources) ? a.sources[0] : null;
  return {
    document: s.document || cite.source_title || primary?.source_title || primary?.filename || 'IBEW Local 15 knowledge base',
    article:  s.article  || cite.article_number || 'N/A',
    section:  s.section  || cite.section_number || 'N/A',
    page:     s.page     || cite.page || 'N/A',
    paragraph:s.paragraph || 'N/A',
  };
}

function pickConfidence(a) {
  if (typeof a.confidence_percent === 'number' && a.confidence_percent > 0) return clamp(a.confidence_percent);
  if (typeof a.confidence === 'number' && a.confidence > 0) return clamp(a.confidence <= 1 ? a.confidence * 100 : a.confidence);
  if (typeof a.confidence_score === 'number' && a.confidence_score > 0) return clamp(a.confidence_score * 100);
  const label = String(a.confidence_label || '').toLowerCase();
  if (label === 'high') return 90;
  if (label === 'medium' || label === 'med') return 70;
  if (label === 'low')  return 50;
  const mode = String(a.mode || a.response_mode || '').toLowerCase();
  const hasQuote = !!(a.exact_contract_wording && String(a.exact_contract_wording.quote || a.exact_contract_wording).trim().length > 20);
  const srcs = Array.isArray(a.sources) ? a.sources.length : 0;
  if (mode === 'contract_grounded' && hasQuote) return 92;
  if (mode === 'contract_grounded')             return 80;
  if (srcs >= 4) return 78;
  if (srcs >= 2) return 68;
  if (srcs >= 1) return 60;
  return 55;
}
function clamp(v) { return Math.max(0, Math.min(100, Number(v) || 0)); }
function firstSentence(text) {
  if (!text) return '';
  const s = String(text).trim();
  const m = s.match(/^(.+?[.!?])\s/);
  return m ? m[1] : (s.length > 200 ? s.slice(0, 200) + '…' : s);
}
function truncateWords(text, maxWords) {
  if (!text) return '';
  const s = String(text).trim();
  const words = s.split(/\s+/);
  if (words.length <= maxWords) return s;
  return words.slice(0, maxWords).join(' ') + ' [excerpt continues]';
}

/* ── Styles ─ landing-page consistent ──────────────────────────────── */
const styles = `
.vac-root {
  display: flex; flex-direction: column; gap: 12px;
  margin-bottom: 18px;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", system-ui, sans-serif;
}

/* Question card — mirrors landing "POPULAR EXAMPLES" card style */
.vac-question {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  background: rgba(15, 27, 70, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-left: 3px solid #22d3ee;
  border-radius: 14px;
}
.vac-q-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px;
  background: rgba(34,211,238,0.10);
  border: 1px solid rgba(34,211,238,0.35);
  border-radius: 999px;
  color: #67e8f9;
  font-size: 10px; font-weight: 800; letter-spacing: 0.14em;
  flex-shrink: 0;
}
.vac-q-text {
  color: #f1f5f9;
  font-size: 14px; font-weight: 600;
  line-height: 1.35;
}

/* Answer card — mirrors landing tile radius / spacing */
.vac-answer-card {
  position: relative;
  background: rgba(15, 27, 70, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 18px;
  padding: 20px 18px 18px;
  box-shadow: 0 6px 22px rgba(0,0,0,0.28);
}

/* Confidence badge (top-right of the Answer card) */
.vac-conf {
  position: absolute;
  top: 14px; right: 14px;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 11px; font-weight: 700;
  font-variant-numeric: tabular-nums;
  z-index: 2;
}
.vac-conf-value { font-size: 12px; font-weight: 800; }
.vac-conf-label { color: rgba(255,255,255,0.65); font-weight: 600; letter-spacing: 0.02em; }
.vac-conf--green  { background: rgba(34,197,94,0.10);   border-color: rgba(34,197,94,0.4);  color: #22c55e; }
.vac-conf--cyan   { background: rgba(34,211,238,0.10);  border-color: rgba(34,211,238,0.4); color: #22d3ee; }
.vac-conf--yellow { background: rgba(250,204,21,0.10);  border-color: rgba(250,204,21,0.4); color: #facc15; }
.vac-conf--orange { background: rgba(251,146,60,0.10);  border-color: rgba(251,146,60,0.4); color: #fb923c; }

/* Block layout — one section per row, dashed separator between */
.vac-block { padding-top: 14px; }
.vac-block:first-of-type { padding-top: 0; padding-right: 100px; /* room for confidence pill */ }
.vac-block + .vac-block { border-top: 1px dashed rgba(255,255,255,0.10); margin-top: 14px; padding-top: 14px; }
.vac-block--answer { padding-top: 0; }

/* Eyebrows — matches landing "POPULAR EXAMPLES" / "TRY ONE OF THESE" */
.vac-eyebrow {
  font-size: 11px; font-weight: 800;
  letter-spacing: 0.18em;
  color: #cbd5e1;
  text-transform: uppercase;
}
.vac-eyebrow--yellow { color: #facc15; }
.vac-eyebrow--orange { color: #fb923c; }
.vac-eyebrow-row {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 10px;
}

/* Small colored icon chips (matches landing example-card icons) */
.vac-icon {
  width: 26px; height: 26px;
  border-radius: 50%;
  display: grid; place-items: center;
  border: 1.5px solid;
  background: rgba(0,0,0,0.28);
  flex-shrink: 0;
}
.vac-icon--blue   { color: #60a5fa; border-color: rgba(96,165,250,0.5); }
.vac-icon--green  { color: #22c55e; border-color: rgba(34,197,94,0.5); }
.vac-icon--orange { color: #fb923c; border-color: rgba(251,146,60,0.5); }

/* Answer sentence — the star of the show */
.vac-sentence {
  margin: 8px 0 0;
  font-size: 18px; font-weight: 800;
  color: #ffffff;
  line-height: 1.35;
  letter-spacing: -0.015em;
}

/* Source chips */
.vac-source-chips {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin-bottom: 10px;
}
.vac-src-chip {
  display: inline-flex; flex-direction: column; gap: 1px;
  padding: 4px 10px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(148,163,184,0.22);
  border-radius: 10px;
  min-width: 0;
}
.vac-src-chip--main {
  background: rgba(96,165,250,0.10);
  border-color: rgba(96,165,250,0.4);
}
.vac-src-lbl {
  font-size: 9px; font-weight: 800;
  letter-spacing: 0.14em;
  color: #94a3b8;
  text-transform: uppercase;
}
.vac-src-val {
  font-size: 12.5px; font-weight: 700;
  color: #f1f5f9;
  font-variant-numeric: tabular-nums;
  max-width: 260px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vac-src-chip--main .vac-src-val { color: #dbeafe; }

/* Supporting quote */
.vac-quote {
  margin: 4px 0 0; padding: 10px 12px;
  background: rgba(0,0,0,0.28);
  border-left: 3px solid rgba(34,211,238,0.55);
  border-radius: 4px;
  color: #d4d4d8;
  font-size: 13px; line-height: 1.55;
  font-style: italic;
  white-space: pre-wrap;
}

/* Plain-english paragraph */
.vac-para {
  margin: 0;
  font-size: 14px; line-height: 1.55;
  color: #e2e8f0;
}

/* Clarify block */
.vac-clarify {
  display: flex; gap: 10px; align-items: flex-start;
  margin-top: 14px; padding: 12px;
  background: rgba(251,146,60,0.08);
  border: 1px solid rgba(251,146,60,0.32);
  border-radius: 12px;
}
.vac-clarify-body {
  display: flex; flex-direction: column; gap: 4px;
  min-width: 0;
}
.vac-clarify-text {
  color: #f1f5f9;
  font-size: 13px; line-height: 1.45;
  font-weight: 500;
}

/* Tablet+ tweaks */
@media (min-width: 640px) {
  .vac-sentence { font-size: 20px; }
  .vac-answer-card { padding: 22px 22px 20px; }
  .vac-src-val { max-width: 340px; }
}
`;
